import { ArrowDownIcon, ArrowRightIcon, ArrowUpRightIcon } from '@phosphor-icons/react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from '@/ui/classNames';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'outline' | 'ghost' | 'quiet';
export type ButtonIcon = 'arrow-up-right' | 'arrow-right' | 'arrow-down' | 'none';

interface ButtonLookProps {
  readonly variant?: ButtonVariant;
  readonly icon?: ButtonIcon;
  readonly children: ReactNode;
}

const ICONS = {
  'arrow-up-right': ArrowUpRightIcon,
  'arrow-right': ArrowRightIcon,
  'arrow-down': ArrowDownIcon,
} as const;

function ButtonContent({
  variant,
  icon,
  loading = false,
  children,
}: Required<Pick<ButtonLookProps, 'variant' | 'icon'>> & {
  readonly loading?: boolean;
  readonly children: ReactNode;
}) {
  const Icon = icon === 'none' ? null : ICONS[icon];
  const glyph = loading ? (
    <span className={styles.spinner} aria-hidden="true" />
  ) : (
    Icon && <Icon size={variant === 'outline' ? 16 : 17} weight="regular" aria-hidden="true" />
  );

  return variant === 'ghost' ? (
    <>
      {glyph && <span className={styles.icon}>{glyph}</span>}
      {children}
    </>
  ) : (
    <>
      {children}
      {glyph && <span className={styles.icon}>{glyph}</span>}
    </>
  );
}

export function ButtonLink({
  variant = 'primary',
  icon = 'arrow-up-right',
  className,
  children,
  ...anchorProps
}: ButtonLookProps & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={cx(styles.button, styles[variant], className)} {...anchorProps}>
      <ButtonContent variant={variant} icon={icon}>
        {children}
      </ButtonContent>
    </a>
  );
}

export function Button({
  variant = 'primary',
  icon = 'arrow-right',
  loading = false,
  className,
  children,
  type = 'button',
  ...buttonProps
}: ButtonLookProps & ButtonHTMLAttributes<HTMLButtonElement> & { readonly loading?: boolean }) {
  return (
    <button
      {...buttonProps}
      type={type}
      className={cx(styles.button, styles[variant], className)}
      aria-busy={loading || undefined}
      disabled={loading || buttonProps.disabled}
    >
      <ButtonContent variant={variant} icon={icon} loading={loading}>
        {children}
      </ButtonContent>
    </button>
  );
}
